/*
    MIT License

    Copyright (c) 2018-present Revel Carlberg West

    Permission is hereby granted, free of charge, to any person obtaining a copy
    of this software and associated documentation files (the "Software"), to deal
    in the Software without restriction, including without limitation the rights
    to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
    copies of the Software, and to permit persons to whom the Software is
    furnished to do so, subject to the following conditions:

    The above copyright notice and this permission notice shall be included in all
    copies or substantial portions of the Software.

    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
    IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
    FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
    AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
    LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
    SOFTWARE.

    -----------------------------------------------------------------------------

    Source code: https://github.com/revelcw/react-hooks-helper

    This hook has been heavily modified & tranformed into TypeScript for our specific needs.
*/

import { useState } from "react";

const error: (e: string) => void = (e: string) => {
    throw new Error(e);
};

const getIndexById: (arr: Array<any>, matchId: string) => number = (arr, matchId) =>
    arr.findIndex(({ id }) => id === matchId);

const isProductionEnv: boolean = process.env.NODE_ENV !== "production";

type UseStepProps = {
    initialStep?: number | string;
    steps: Array<number | string> | number;
};

type UseStepReturn = {
    index: number;
    step: number | string | {};
    navigation: Navigation;
};

export const useStep: (props: UseStepProps) => UseStepReturn = ({
    initialStep = 0, // accept number or string (if string, convert to index)
    steps: stepsProp,
}) => {
    if (isProductionEnv) {
        if (!Array.isArray(stepsProp) && !Number.isInteger(stepsProp)) {
            error("useStep: You must specify either an array or an integer for `steps`");
        }
    }

    // Convert steps to an object array if it is a number.
    const steps: Array<number | string | {}> =
        typeof stepsProp === "number" ? new Array(stepsProp).fill({}) : stepsProp;

    // Compute initialStepIndex in case an id is passed vs an index.
    const initialStepIndex: number = typeof initialStep === "number" ? initialStep : getIndexById(steps, initialStep);

    if (isProductionEnv) {
        if (typeof initialStep === "string" && initialStepIndex === -1) {
            error(`useStep: id of "${initialStep}" specified in initialStep not found in steps`);
        }
    }

    // Setup state.
    const [index, setStep] = useState<number>(initialStepIndex);

    const step: number | string | {} = steps[index];

    const deltaSetStep: (delta: number) => void = (delta = 1) => {
        setStep((index + steps.length + delta) % steps.length);
    };

    // Build navigation callback functions.
    const navigation: Navigation = {
        next: () => deltaSetStep(1),
        previous: () => deltaSetStep(-1),
        go: (newStep: number | string) => {
            if (typeof newStep === "number") {
                if (isProductionEnv) {
                    if (newStep < 0 || newStep > steps.length) {
                        error(`useStep: Index out of range in go(${newStep})`);
                    }
                }

                setStep(newStep);
            } else {
                const newStepId: number = getIndexById(steps, newStep);

                if (isProductionEnv) {
                    if (newStepId === -1) {
                        error(`useStep: go("${newStep}") not found in steps`);
                    }
                }

                setStep(newStepId);
            }
        },
    };

    return {
        index,
        step,
        navigation,
    };
};

type Navigation = {
    next: () => void;
    previous: () => void;
    go: (newStep: number | string) => void;
};
